// Started from shadcn/ui (new-york-v4) "sonner", restyled to Union. Toasts confirm what just
// happened, in the voice of DESIGN.md ("Casey moved to 12:24."), with Undo where it can be
// undone. Errors that need a fix belong next to the thing, not in a toast.
//
//   <Toaster />  once per app;  toast.success("…"), toast.error("…"), toast("…")

import { cn } from "@union/ui/lib/utils";
import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Toaster as Sonner, type ToasterProps } from "sonner";

const readTheme = (): NonNullable<ToasterProps["theme"]> => {
  const theme = document.documentElement.dataset.theme;
  return theme === "light" || theme === "dark" ? theme : "system";
};

/** Follows data-theme on <html> (or the system), like the rest of Union. */
function useDocumentTheme(): NonNullable<ToasterProps["theme"]> {
  const [theme, setTheme] = useState(readTheme);
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(readTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);
  return theme;
}

const icon = "size-4 [stroke-width:1.75]";

function Toaster({ toastOptions, ...props }: ToasterProps) {
  return (
    <Sonner
      theme={useDocumentTheme()}
      position="bottom-center"
      icons={{
        success: <CircleCheckIcon className={cn(icon, "text-success")} />,
        info: <InfoIcon className={cn(icon, "text-muted-foreground")} />,
        warning: <TriangleAlertIcon className={cn(icon, "text-warning")} />,
        error: <OctagonXIcon className={cn(icon, "text-destructive")} />,
        loading: <Loader2Icon className={cn(icon, "animate-spin text-muted-foreground")} />,
      }}
      toastOptions={{
        ...toastOptions,
        classNames: {
          toast:
            "!rounded-md !border !border-border !bg-card !text-foreground !shadow-float !type-body !gap-space-2 !p-space-3",
          description: "!type-caption !text-muted-foreground",
          actionButton: "!rounded-sm !bg-primary !text-primary-foreground !type-label",
          cancelButton: "!rounded-sm !bg-muted !text-foreground !type-label",
          ...toastOptions?.classNames,
        },
      }}
      {...props}
    />
  );
}

export { Toaster };
